import { useParams, useHistory } from 'react-router-dom';
import * as ROUTES from '../../../constants/routes';

// @flow
import { useEffect, useState } from 'react';
import styled, { withTheme } from 'styled-components';
import { Navbar, NavbarGroup, NavbarHeading } from '@blueprintjs/core';
import Layout from '../../layouts/default-logged-in';
import { breakpoints } from '../../../styles/variables';
import { trackEvent, ENTER_PAGE } from '../../../services/mixpanel';
import * as LocationModel from '../../../models/location';
import CampaignManagerComponent from '../../campaign-manager-components/index';
import { CAMPAIGNS_TEXT_CONTENT } from '../../../constants/campaigns';
import { FilterOptionsProvider } from '../../campaign-manager-components/useFilterOptionsContext';
import { errorToast } from '../../../utils/toaster';
import { getDayEnd } from '../../../utils/dates';
import * as api from '../../../services/api';
import useLoadingHandler from '../../../hooks/use-loading-handler';

class CampaignCantBeCalculatedError extends Error {
  constructor(message) {
    super(message);
    this.name = 'CampaignCantBeCalculatedError';
  }
}

const TopBar = styled(Navbar)`
  padding-left: 2em;
  padding-right: 2em;
  @media (max-width: ${breakpoints.TABLET_SIZE}) {
    padding: 1em;
  }
`;

const INITIAL_STATE = {};

const ERROR_MESSAGES = {
  FETCH:
    'Could not get your campaign. Try refreshing your browser or contact support.',
  SAVE: 'Could not save your campaign. Try again or contact support.',
  CANT_BE_CALCULATED:
    'Campaign uplift cannot be calculated due to lack of data.',
};

type Props = {
  locations: LocationModel.t[],
  recordings: RecordingModel.t[],
  showStaffFilters: boolean,
  disableAgeAndGenderOnFrontEnd: boolean,
  availableMetrics: any,
};

const transformPeriodDates = (period, transformFn) => ({
  ...period,
  ...(period.selectedDates && {
    selectedDates: transformFn(period.selectedDates),
  }),
  ...(period.customPeriodDates && {
    customPeriodDates: transformFn(period.customPeriodDates),
  }),
});

const periodsToISO = (dateRange) => ({
  start: new Date(dateRange.start).toISOString(),
  end: getDayEnd(new Date(dateRange.end)).toISOString(),
});

const periodsFromISO = (dates) => ({
  start: dates.start.split('T')[0],
  end: dates.end.split('T')[0],
});

const formatPeriodsToISO = (period) =>
  transformPeriodDates(period, periodsToISO);

const formatPeriodsFromISO = (period) =>
  transformPeriodDates(period, periodsFromISO);

const formatCampaign = (campaign) => ({
  ...campaign,
  // Required as control dates are of form YYYY-MM-DD and the DB requires YYYY-MM-DDTHH:mm:ss.sssZ
  ...(campaign.testPeriod && {
    testPeriod: formatPeriodsFromISO(campaign.testPeriod),
  }),
  ...(campaign.controlPeriod && {
    controlPeriod: formatPeriodsFromISO(campaign.controlPeriod),
  }),
});

const createCampaignObject = (filterOptions, campaignUplift) => {
  const {
    name,
    taxonomies,
    testLocations,
    controlLocations,
    selectedBreakdowns,
    metric,
    lineTaxonomies,
  } = filterOptions;
  return {
    name,
    description: '',
    metric,
    taxonomies,
    lineTaxonomies,
    testLocations,
    controlLocations,
    // Required as control dates are of form YYYY-MM-DD and the DB requires YYYY-MM-DDTHH:mm:ss.sssZ
    testPeriod: formatPeriodsToISO(filterOptions.testPeriod),
    controlPeriod: formatPeriodsToISO(filterOptions.controlPeriod),
    selectedBreakdowns,
    campaignUplift,
  };
};

const resolveCampaignUplift = (filterOptions, uplift) => {
  const hasControlLocations = filterOptions?.controlLocations?.length > 0;
  const hasControlPeriod = filterOptions?.controlPeriod?.active;
  let campaignUplift = null;

  if (hasControlPeriod && hasControlLocations) {
    campaignUplift = uplift.overallUpliftDelta;
  }

  if (hasControlPeriod && !hasControlLocations) {
    campaignUplift = uplift.testUplift;
  }

  if (!hasControlPeriod && hasControlLocations) {
    campaignUplift = uplift.locationUplift;
  }

  if (campaignUplift === null) {
    throw new CampaignCantBeCalculatedError(ERROR_MESSAGES.CANT_BE_CALCULATED);
  }

  return campaignUplift;
};

const SingleCampaignPage = ({
  locations,
  recordings,
  showStaffFilters,
  disableAgeAndGenderOnFrontEnd,
  availableMetrics,
}: Props) => {
  const history = useHistory();
  const { startLoading, stopLoading } = useLoadingHandler();
  const [campaign, setCampaign] = useState(INITIAL_STATE);
  const { campaignId } = useParams();

  const getCampaign = async () => {
    const key = 'fetch-single-campaign';
    if (campaignId === 'new') {
      setCampaign(null);
      return;
    }

    try {
      startLoading(key);
      const result = await api.campaigns.get(campaignId);
      setCampaign(formatCampaign(result));
    } catch (error) {
      errorToast({
        message: ERROR_MESSAGES.FETCH,
      });
    } finally {
      stopLoading(key);
    }
  };

  useEffect(() => {
    trackEvent(ENTER_PAGE, 'SINGLE_CAMPAIGN');
    getCampaign();
  }, [campaignId]);

  const saveCampaign = async (filterOptions, uplift) => {
    try {
      const resolvedUplift = resolveCampaignUplift(filterOptions, uplift);
      const campaign = createCampaignObject(filterOptions, resolvedUplift);
      await (campaignId === 'new'
        ? api.campaigns.create(campaign)
        : api.campaigns.update(campaignId, campaign));

      history.push(ROUTES.CAMPAIGNS);
    } catch (error) {
      let errorMessage = ERROR_MESSAGES.SAVE;
      if (error instanceof CampaignCantBeCalculatedError) {
        errorMessage = error.message;
      }

      errorToast({
        message: errorMessage,
      });
    }
  };

  return (
    <Layout>
      {locations.length ? (
        <FilterOptionsProvider
          locations={locations}
          showStaffFilters={showStaffFilters}
          disableAgeAndGenderOnFrontEnd={disableAgeAndGenderOnFrontEnd}
        >
          <CampaignManagerComponent
            locations={locations}
            recordings={recordings}
            showStaffFilters={showStaffFilters}
            disableAgeAndGenderOnFrontEnd={disableAgeAndGenderOnFrontEnd}
            availableMetrics={availableMetrics}
            campaign={campaign}
            saveCampaign={saveCampaign}
          />
        </FilterOptionsProvider>
      ) : (
        <TopBar>
          <NavbarGroup>
            <NavbarHeading>{CAMPAIGNS_TEXT_CONTENT.NO_LOCATIONS}</NavbarHeading>
          </NavbarGroup>
        </TopBar>
      )}
    </Layout>
  );
};

export default withTheme(SingleCampaignPage);
